import { PhotoSubmissionsList, RemovalRequestsList } from '@/components/photo-submissions';
import {
  PageContainer,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Badge,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

const breadcrumbs = [
  { label: 'Home', href: '/' },
  { label: 'Photo Submissions', current: true },
];

type SubmissionStatus = 'pending' | 'approved' | 'rejected' | 'all';

/**
 * Photo Submissions list page.
 *
 * Submissions are scoped by a status segmented control (Pending / Approved /
 * Rejected / All) that sits inline with the list header — where a list's
 * action buttons normally go — so a reviewer can work a single queue and clear
 * it. Defaults to Pending (the actionable queue) and scopes the list
 * server-side via `defaultWhere`.
 *
 * Internal organization users only.
 */
export function PhotoSubmissionsPage() {
  const apiClient = useApiClient();
  const [submissionStatus, setSubmissionStatus] = useState<SubmissionStatus>('pending');

  // Pending counts power the badges that tell a reviewer how much is left.
  const { data: pendingSubmissionsCount = 0 } = useQuery({
    queryKey: ['PhotoSubmission', 'pending-count'],
    queryFn: async () => {
      const response = (await apiClient.post('/photo-submissions/list', {
        where: { status: 'pending' },
        limit: 1,
      })) as { pagination?: { total?: number } };
      return response.pagination?.total ?? 0;
    },
  });

  const { data: removalRequestsData } = useQuery({
    queryKey: ['photo-removal-requests', 'pending'],
    queryFn: async () => {
      const response = (await apiClient.get('/photo-removal-requests?filters[status]=pending')) as {
        data: { pagination?: { total: number } };
      };
      return response.data;
    },
  });

  const pendingRemovalCount = removalRequestsData?.pagination?.total || 0;

  // Segmented status control rendered inline in the list header's action slot.
  const statusControl = (
    <Tabs
      value={submissionStatus}
      onValueChange={value => setSubmissionStatus(value as SubmissionStatus)}
    >
      <TabsList>
        <TabsTrigger value="pending" className="flex items-center gap-2">
          Pending
          {pendingSubmissionsCount > 0 && (
            <Badge variant="warning" className="ml-1 h-5 min-w-5 px-1.5">
              {pendingSubmissionsCount}
            </Badge>
          )}
        </TabsTrigger>
        <TabsTrigger value="approved">Approved</TabsTrigger>
        <TabsTrigger value="rejected">Rejected</TabsTrigger>
        <TabsTrigger value="all">All</TabsTrigger>
      </TabsList>
    </Tabs>
  );

  return (
    <PageContainer>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Photo Submissions</h1>
          <p className="text-muted-foreground">Review and manage photo submissions from users</p>
        </div>

        <Tabs defaultValue="submissions" className="w-full">
          <TabsList>
            <TabsTrigger value="submissions">Submissions</TabsTrigger>
            <TabsTrigger value="removal-requests" className="flex items-center gap-2">
              Removal Requests
              {pendingRemovalCount > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 min-w-5 px-1.5">
                  {pendingRemovalCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="submissions" className="mt-6">
            <PhotoSubmissionsList
              breadcrumbs={breadcrumbs}
              headerRight={statusControl}
              defaultWhere={submissionStatus === 'all' ? undefined : { status: submissionStatus }}
            />
          </TabsContent>

          <TabsContent value="removal-requests" className="mt-6">
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold">Pending Requests</h2>
                <p className="text-sm text-muted-foreground">
                  Users have requested to remove their approved photos
                </p>
              </div>
              <RemovalRequestsList status="pending" />

              <div className="pt-6">
                <h2 className="text-xl font-semibold">Processed Requests</h2>
                <p className="text-sm text-muted-foreground">
                  Previously reviewed removal requests
                </p>
              </div>
              <Tabs defaultValue="all" className="w-full">
                <TabsList>
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="approved">Approved</TabsTrigger>
                  <TabsTrigger value="rejected">Rejected</TabsTrigger>
                </TabsList>
                <TabsContent value="all" className="mt-4">
                  <RemovalRequestsList status="processed" />
                </TabsContent>
                <TabsContent value="approved" className="mt-4">
                  <RemovalRequestsList status="approved" />
                </TabsContent>
                <TabsContent value="rejected" className="mt-4">
                  <RemovalRequestsList status="rejected" />
                </TabsContent>
              </Tabs>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PageContainer>
  );
}
